import type {
  AbilityRecord,
  CompiledManifest,
  Mode,
} from "../../shared/api/generated/contracts";
import { useT } from "../../shared/i18n";
import { Badge, Button, EmptyState, ErrorState, Icons } from "../../shared/ui";
import { hiddenFieldIds } from "../renderer/fields";

function stringValue(value: unknown): string {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  return "";
}

function renderElement(
  element: Record<string, unknown>,
  records: readonly AbilityRecord[],
  index: number,
  hiddenIds: ReadonlySet<string>,
) {
  const kind = stringValue(element.kind);
  const text = stringValue(element.text ?? element.label);
  if (kind === "heading")
    return (
      <h2 key={index} className="text-h3">
        {text}
      </h2>
    );
  if (kind === "text") return <p key={index}>{text}</p>;
  if (kind === "divider") return <hr key={index} />;
  if (kind === "page_break")
    return <div key={index} className="break-after-page" aria-hidden="true" />;
  if (kind === "page_number") return null;
  if (kind === "field_value") {
    const fieldId = stringValue(element.field_id);
    if (hiddenIds.has(fieldId)) return null;
    return (
      <p key={index}>
        {text && <strong>{text}: </strong>}
        {stringValue(records[0]?.payload[fieldId])}
      </p>
    );
  }
  if (kind === "table" || kind === "related_table") {
    const fieldIds = Array.isArray(element.fields)
      ? element.fields.filter(
          (field): field is string =>
            typeof field === "string" && !hiddenIds.has(field),
        )
      : [];
    if (fieldIds.length === 0) return null;
    return (
      <table key={index} className="w-full border-collapse text-left">
        <thead>
          <tr>
            {fieldIds.map((field) => (
              <th key={field}>{field}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.id}>
              {fieldIds.map((field) => (
                <td key={field}>{stringValue(record.payload[field])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (kind === "summary") return <p key={index}>{text}</p>;
  return null;
}

/**
 * Print Mode is a separate, isolated token set: always light, always high
 * contrast, inheriting neither the app's light nor its dark palette. The
 * surrounding shell follows the theme; the paper never does.
 *
 * `window.print()` is only ever reached from an explicit user action, and no
 * server-side PDF is produced.
 */
export function PrintSurface({
  manifest,
  formId,
  records = [],
  mode,
  onPrint,
  canPrint = true,
  onOpenExternal,
  onAskAssistant,
}: {
  manifest: CompiledManifest;
  formId: string;
  records?: readonly AbilityRecord[];
  mode: Mode;
  onPrint: () => void;
  canPrint?: boolean;
  onOpenExternal?: () => void;
  /** Print forms are added through the assistant in the chat. */
  onAskAssistant?: () => void;
}) {
  const t = useT();
  if (manifest.print_forms.length === 0)
    return (
      <main aria-label={t("print.title")}>
        <EmptyState
          icon={Icons.Print}
          iconTone="accent"
          title={t("print.noForms.title")}
          body={t("print.noForms.body")}
          action={
            onAskAssistant && (
              <Button onClick={onAskAssistant}>
                {t("print.noForms.action")}
              </Button>
            )
          }
        />
      </main>
    );
  const form = manifest.print_forms.find(
    (candidate) => candidate.id === formId,
  );
  if (!form)
    return (
      <main aria-label={t("print.title")}>
        <ErrorState
          icon={Icons.Warning}
          iconTone="warning"
          title={t("print.title")}
          body={t("print.notFound")}
        />
      </main>
    );
  const label = typeof form.label === "string" ? form.label : t("print.title");
  const source =
    typeof form.source === "object" && form.source !== null
      ? (form.source as Record<string, unknown>)
      : {};
  const maxRows =
    typeof source.max_rows === "number" ? Math.max(0, source.max_rows) : 0;
  const collectionId = stringValue(source.collection_id);
  const scopedRecords = records
    .filter((record) => record.collection_id === collectionId)
    .slice(0, maxRows);
  const hiddenIds = hiddenFieldIds(
    manifest.collections.find((collection) => collection.id === collectionId),
  );
  const sections = Array.isArray(form.sections)
    ? form.sections.filter(
        (section): section is Record<string, unknown> =>
          typeof section === "object" && section !== null,
      )
    : [];
  if (!canPrint)
    return (
      <main aria-label={t("print.title")}>
        <ErrorState
          icon={Icons.Warning}
          iconTone="warning"
          title={t("print.unsupported.title")}
          body={t("print.unsupported.body")}
          action={
            onOpenExternal && (
              <Button onClick={onOpenExternal}>
                <Icons.ExternalLink size={18} className="mr-2" />
                {t("print.openExternal")}
              </Button>
            )
          }
        />
      </main>
    );
  return (
    <main aria-label={t("print.title")}>
      <div data-print="hidden" className="mb-4 flex flex-col items-start gap-3">
        <Badge tone={mode === "preview" ? "warning" : "neutral"}>
          {mode === "preview" ? t("print.preview") : t("print.private")}
        </Badge>
        <Button onClick={onPrint} className="w-full">
          <Icons.Print size={18} className="mr-2" />
          {t("print.action")}
        </Button>
      </div>
      <div className="print-surface rounded-lg shadow-card print:rounded-none print:shadow-none">
        <div className="print-page">
          {mode === "preview" && (
            <p className="print-draft-stamp">{t("print.draftStamp")}</p>
          )}
          <h1 className="print-title">{label}</h1>
          {sections.map((section, sectionIndex) => {
            const elements = Array.isArray(section.elements)
              ? section.elements.filter(
                  (element): element is Record<string, unknown> =>
                    typeof element === "object" && element !== null,
                )
              : [];
            return (
              <section key={stringValue(section.id) || String(sectionIndex)}>
                {typeof section.title === "string" && (
                  <h2 className="text-h3">{section.title}</h2>
                )}
                {elements.map((element, index) =>
                  renderElement(element, scopedRecords, index, hiddenIds),
                )}
              </section>
            );
          })}
          <p className="print-footnote">
            {t("print.page")} 1 {t("print.of")} 1
          </p>
        </div>
      </div>
    </main>
  );
}
